import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Loader,
  buildNotification,
  GenerateNotification,
  isFromValid,
  isValidate,
} from "smart-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Switch } from "@/ui/switch";
import { cn } from "@/lib/utils";
import { ChevronDown, Save, ArrowLeft, Trash2 } from "lucide-react";

import TenantDropdown from "../../../Core/Components/TenantDropdown/TenantDropdown";
import DesktopLayout from "../Components/Layout/DesktopLayout";

import {
  ValidatePermissions,
  findTenant,
  findTenantById,
} from "../../../../Utils/Auth/ValidatePermissions";

import {
  getRFProfile,
  createRFProfile,
  updateRFProfile,
  deleteRFProfile,
} from "../Services/RFProfileService";
import {
  objectToBase64,
  base64ToObject,
} from "../../../../Utils/Common/commonUtils";

import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../../constants/eventDataTypes";
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
  DELETE_MESSAGE,
} from "../../../../constants/notificationMessages";

// Types
type ProfileGroupRef = {
  TenantId: string | number;
  tenant_name?: string;
  SRFProfileGroupId: string | number;
  SRFProfileGroupName?: string;
};

type RouteState = {
  edit?: boolean;
  dataSet?: any; // server payload for profile detail when editing
  profileGroup?: ProfileGroupRef;
};

type ProfileForm = {
  TenantId: string | number;
  TenantName?: string;
  SRFProfileId?: string | number;
  SRFProfilesName: string;
  SRFProfilesDetails?: string;
  AppURL?: string;
  SRFProfileGroupName?: string;
  SRFProfileGroupId?: string | number;
  Active: boolean | number;
  [k: string]: any;
};

const requiredFields: (keyof ProfileForm)[] = ["SRFProfilesName", "AppURL"];

const AddRFProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as { state?: RouteState; search: string };
  const { state } = location;
  const { tenantId } = useParams();
  const params = new URLSearchParams(location.search || "");
  const refId = params.get("refId");
  const profileGroupParam = params.get("profileGroup");
  const group = React.useMemo(
    () =>
      state?.profileGroup ||
      (profileGroupParam
        ? (base64ToObject as any)(profileGroupParam)
        : undefined),
    [state?.profileGroup, profileGroupParam],
  ) as any;
  const groupParam = React.useMemo(
    () => profileGroupParam || (group ? objectToBase64(group) : ""),
    [profileGroupParam, group],
  );

  // UI/flow state
  const [selected, setSelected] = React.useState<number>(0);
  const [shouldUpdate, setShouldUpdate] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
  const [detailsDataModified, setDetailsDataModified] =
    React.useState<boolean>(false);
  const [validateForm, setValidateForm] = React.useState<boolean>(false);

  // Tenant + form state
  const [tenant, setTenant] = React.useState<any>(
    findTenantById((group as any)?.TenantId as any),
  );

  const [formData, setFormData] = React.useState<ProfileForm>({
    TenantId: (group as any)?.TenantId as any,
    TenantName: (group as any)?.tenant_name,
    SRFProfileId: "",
    SRFProfilesName: "",
    SRFProfilesDetails: "",
    AppURL: "",
    SRFProfileGroupName: (group as any)?.SRFProfileGroupName,
    SRFProfileGroupId: (group as any)?.SRFProfileGroupId,
    Active: true,
  });

  const [tempState, setTempState] = React.useState<RouteState | undefined>(
    state,
  );

  // Tabs (if still used by DesktopLayout)
  const handleSelect = (e: any) => {
    setSelected(e?.selected ?? 0);
    setShouldUpdate((v) => !v);
  };

  // Load detail when editing (by state or by refId deep link)
  React.useEffect(() => {
    const loadByState = async () => {
      const { dataSet } = state as any;
      try {
        setLoading(true);
        const response: any = await getRFProfile({
          data: dataSet,
          moduleName: "RFProfile",
          isServiceWorker: false,
          listController: undefined,
        });
        const rfProfileData = response?.Payload ?? dataSet;
        setTempState({ ...state, dataSet: rfProfileData });
        setFormData(rfProfileData as ProfileForm);
        setTenant(findTenant(rfProfileData as any));
        if (ValidatePermissions(rfProfileData as any)) {
          setDetailsDataModified(true);
        }
        setShouldUpdate((v) => !v);
      } finally {
        setLoading(false);
      }
    };

    const loadByRef = async (rid: string) => {
      try {
        setLoading(true);
        const decoded = base64ToObject(rid as any);
        const response: any = await getRFProfile({
          data: decoded,
          moduleName: "RFProfile",
          isServiceWorker: false,
          listController: undefined,
        });
        const rfProfileData = response?.Payload ?? decoded;
        setTempState({ edit: true, dataSet: rfProfileData });
        setFormData(rfProfileData as ProfileForm);
        setTenant(findTenant(rfProfileData as any));
        if (ValidatePermissions(rfProfileData as any)) {
          setDetailsDataModified(true);
        }
      } finally {
        setLoading(false);
      }
    };

    if (state?.dataSet) {
      loadByState();
    } else if (refId) {
      loadByRef(refId);
    } else {
      setDetailsDataModified(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.dataSet, refId]);

  // Keep tenant in sync when not editing
  React.useEffect(() => {
    if (!state?.edit && (group as any)?.TenantId) {
      const t: any = (findTenantById as any)((group as any)?.TenantId);
      setTenant(t);
      setFormData((prev) => ({
        ...prev,
        TenantId: (group as any)?.TenantId as any,
        TenantName: t?.TenantName,
      }));
    }
  }, [state?.edit, group?.TenantId]);

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value?: any; type?: string; checked?: any } },
  ) => {
    const { name, value, type } = e.target as any;
    const nextValue =
      type === "checkbox" ? ((e.target as any).checked ? 1 : 0) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const onExit = () =>
    navigate(`/smartrf/profile-group/${group?.TenantId}?refId=${groupParam}`, {
      state: { edit: true, dataSet: group },
    });

  // Split save button with dropdown ("Save" default, "Save & Close" in menu)
  const SplitSaveButton: React.FC<{
    onSave: (opts: { isExit?: boolean; isContinue?: boolean }) => void;
    disabled?: boolean;
  }> = ({ onSave, disabled }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      if (!open) return;
      const onDoc = (ev: MouseEvent) => {
        const t = ev.target as Node;
        if (ref.current && !ref.current.contains(t)) setOpen(false);
      };
      const onKey = (ev: KeyboardEvent) => {
        if (ev.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDoc);
        document.removeEventListener("keydown", onKey);
      };
    }, [open]);

    return (
      <div ref={ref} className="relative inline-block">
        {/* Dashboard-like split button styling using theme tokens (works in light/dark) */}
        <div className="inline-flex overflow-hidden rounded-md border border-border bg-primary text-primary-foreground shadow-sm">
          <button
            type="button"
            onClick={() => onSave({ isContinue: true })}
            disabled={disabled}
            className="h-9 px-4 text-sm font-medium bg-transparent text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            className="h-9 w-9 grid place-items-center bg-transparent text-primary-foreground border-l border-border/50 hover:bg-primary/90 disabled:opacity-50"
            aria-haspopup="menu"
            aria-expanded={open ? "true" : "false"}
            onClick={() => setOpen((v) => !v)}
            disabled={disabled}
            title="More save options"
          >
            <span className="sr-only">Toggle save menu</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                open && "rotate-180",
              )}
              strokeWidth={2.5}
            />
          </button>
        </div>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-2 min-w-[14rem] rounded-xl bg-popover text-popover-foreground shadow-[0_10px_30px_rgba(0,0,0,0.45)] ring-1 ring-border/50 z-[300]"
          >
            <button
              type="button"
              role="menuitem"
              className="flex w-full h-9 items-center gap-3 rounded-md px-3 py-2.5 text-sm hover:bg-accent/60 hover:text-accent-foreground focus:bg-accent/60 focus:outline-none cursor-pointer"
              onClick={() => {
                onSave({ isExit: true });
                setOpen(false);
              }}
            >
              <Save className="h-4 w-4" />
              <span className="truncate ">Save & Close</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const onSave = async ({
    isExit = false,
    isContinue = false,
  }: {
    isExit?: boolean;
    isContinue?: boolean;
  }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    setLoading(true);
    let notificationMetaData: any = {};
    const data = { ...formData };
    let response: any;
    let method: typeof createRFProfile | typeof updateRFProfile;

    if (state?.edit) {
      method = updateRFProfile as any;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createRFProfile as any;
      notificationMetaData = CREATE_MESSAGE;
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        moduleName: "RFProfile",
        isServiceWorker: false,
        listController: undefined,
      });

      if (!response?.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response?.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );

        // reset when creating and staying on page
        if (!state?.edit && !isExit && !isContinue) {
          setFormData({
            TenantId: state?.profileGroup?.TenantId as any,
            TenantName: state?.profileGroup?.tenant_name,
            SRFProfileId: "",
            SRFProfilesName: "",
            SRFProfilesDetails: "",
            AppURL: "",
            SRFProfileGroupName: state?.profileGroup?.SRFProfileGroupName,
            SRFProfileGroupId: state?.profileGroup?.SRFProfileGroupId,
            Active: true,
          });
        }

        // Update tempState to trigger re-render
        setTempState({
          ...state,
          dataSet: response?.Payload,
        });

        if (isContinue) {
          const payload = response?.Payload;
          const profileRef = objectToBase64({
            SRFProfileId: payload?.SRFProfileId,
            TenantId: payload?.TenantId,
          });
          navigate(
            `/smartrf/profile/${payload?.TenantId}?refId=${profileRef}&profileGroup=${groupParam}`,
            {
              state: {
                edit: true,
                dataSet: payload,
                profileGroup: group,
              },
            },
          );
        }
        if (isExit) {
          navigate(
            `/smartrf/profile-group/${group?.TenantId}?refId=${groupParam}`,
            {
              state: {
                edit: true,
                dataSet: group,
              },
            },
          );
        }
      }
    } catch (err) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  const onConfirmDelete = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const resp: any = await deleteRFProfile({
        data: formData,
        moduleName: "RFProfile",
        isServiceWorker: false,
        listController: undefined,
      });
      if (resp?.IsSuccess) {
        GenerateNotification(
          buildNotification(DELETE_MESSAGE),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        navigate(
          `/smartrf/profile-group/${group?.TenantId}?refId=${groupParam}`,
          {
            state: { edit: true, dataSet: group },
          },
        );
      } else {
        GenerateNotification(
          buildNotification({
            title: resp?.Title,
            description: resp?.Message,
            style: resp?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      }
    } catch {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="w-full pl-4 py-4"
      style={{ width: "calc(50vw + 18rem - 16px)", maxWidth: "100%" }}
    >
      {loading && (
        <div className="py-8 flex items-center justify-center">
          <Loader />
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onExit}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted/40 hover:bg-muted/60 text-foreground/90 ring-1 ring-border/60 cursor-pointer"
            aria-label="Back"
            title="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
                Profile
              </h1>
              <span className="text-muted-foreground">/</span>
              <p className="text-xl sm:text-2xl font-medium text-foreground truncate">
                {formData?.SRFProfilesName || "New"}
              </p>
            </div>
          </div>
        </div>

        {state?.edit && detailsDataModified && (
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-block rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground">
              Editing
            </span>
            <Button
              type="button"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmOpen(true)}
              title="Delete"
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>

      {/* Details card */}
      <div className="mt-4 rounded-md border border-border bg-background shadow-sm">
        <div className="p-4 space-y-4 lg:mr-[18rem]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tenant */}
            <div
              className={cn(
                "space-y-1",
                isValidate(validateForm, formData?.TenantName) &&
                  "text-destructive",
              )}
            >
              <TenantDropdown
                textField="TenantName"
                label="Tenant"
                dataItemKey="TenantId"
                className="tenant-dropdown"
                tenant={tenant}
                setTenant={setTenant}
                defaultItem={{
                  TenantName: "Select Tenant ...",
                  disabled: true,
                }}
                editMode={"ReadOnly"}
                filterable={true}
                tenantFormId="TenantId"
                tenantFormName="TenantName"
                setFormData={setFormData}
                requiredField={true}
              />
              {isValidate(validateForm, formData?.TenantName) && (
                <p className="text-xs mt-1">This field cannot be empty</p>
              )}
            </div>

            {/* Profile Group (Read-only) */}
            <div className="space-y-1">
              <Label
                htmlFor="SRFProfileGroupName"
                className="text-sm font-medium text-muted-foreground"
              >
                Profile Group
              </Label>
              <Input
                id="SRFProfileGroupName"
                name="SRFProfileGroupName"
                value={(group as any)?.SRFProfileGroupName || ""}
                disabled
                className="h-10 bg-background "
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Profile Name */}
            <div
              className={cn(
                "space-y-1",
                isValidate(validateForm, formData?.SRFProfilesName) &&
                  "text-destructive",
              )}
            >
              <Label
                htmlFor="SRFProfilesName"
                className="text-sm font-medium text-muted-foreground"
              >
                Profile Name*
              </Label>
              <Input
                id="SRFProfilesName"
                name="SRFProfilesName"
                value={formData?.SRFProfilesName || ""}
                onChange={handleInputChange}
                disabled={!detailsDataModified}
                className="h-10 bg-background"
              />
              {isValidate(validateForm, formData?.SRFProfilesName) && (
                <p className="text-xs mt-1">This field cannot be empty</p>
              )}
            </div>

            {/* App URL */}
            <div
              className={cn(
                "space-y-1",
                isValidate(validateForm, formData?.AppURL) &&
                  "text-destructive",
              )}
            >
              <Label
                htmlFor="AppURL"
                className="text-sm font-medium text-muted-foreground"
              >
                App URL*
              </Label>
              <Input
                id="AppURL"
                name="AppURL"
                value={formData?.AppURL || ""}
                onChange={handleInputChange}
                disabled={!detailsDataModified}
                className="h-10 bg-background"
              />
              {isValidate(validateForm, formData?.AppURL) && (
                <p className="text-xs mt-1">This field cannot be empty</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label
              htmlFor="SRFProfilesDetails"
              className="text-sm font-medium text-muted-foreground"
            >
              Description
            </Label>
            <textarea
              id="SRFProfilesDetails"
              name="SRFProfilesDetails"
              value={formData?.SRFProfilesDetails || ""}
              onChange={handleInputChange}
              disabled={!detailsDataModified}
              className="h-24 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground shadow-sm focus:outline-none leading-[1.4]"
            />
          </div>

          {/* Active */}
          <div className="space-y-1">
            <Label
              htmlFor="Active"
              className="text-sm font-medium text-muted-foreground"
            >
              Active
            </Label>
            <div className="pt-1">
              <Switch
                id="Active"
                checked={!!formData?.Active}
                onCheckedChange={(v: boolean) =>
                  setFormData((prev) => ({ ...prev, Active: v ? 1 : 0 }))
                }
                disabled={!detailsDataModified}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        {detailsDataModified && (
          <div className="px-4 py-3 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onExit}
              className="h-9 px-4 text-sm font-medium"
            >
              Cancel
            </Button>
            <SplitSaveButton
              onSave={onSave}
              disabled={loading || !detailsDataModified}
            />
          </div>
        )}
      </div>

      {/* Detail grid section (unchanged legacy DesktopLayout) */}
      {tempState?.edit && (
        <div className="">
          <div className="p-3 !px-0">
            <DesktopLayout
              // Tailwind-only DesktopLayout (no Kendo)
              handleSelect={handleSelect as any}
              selected={selected as any}
              detailsDataModified={detailsDataModified as any}
              state={tempState as any}
            />
          </div>
        </div>
      )}

      {/* Delete confirmation (shadcn-like dialog) */}
      {confirmOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setConfirmOpen(false)}
          />
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby="confirm-desc"
          >
            <div
              className="w-full max-w-sm rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-border">
                <h2 id="confirm-title" className="text-base font-semibold">
                  Delete profile?
                </h2>
              </div>
              <div className="px-4 py-3">
                <p id="confirm-desc" className="text-sm text-muted-foreground">
                  This action cannot be undone. The record will be permanently
                  removed.
                </p>
              </div>
              <div className="px-4 py-3 flex justify-end gap-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onConfirmDelete}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddRFProfile;
